import type * as THREE from 'three'
import { SceneAuxiliaryObjectRegistry } from '../../scene-object'
import type { EntityHighlightTarget } from '../types/entity-highlight-target'
import type {
  ImportedModelFileInfoMetadata,
  ImportedModelObjectHierarchyMetadata,
  ImportedModelObjectMetadata,
  ImportedModelSelectionTargetMetadata,
} from './ImportedModelObjectMetadataTypes'
import {
  createImportedBodyOwnershipLookupKeys,
  resolveStableImportedBodyBindingId,
} from '../utils'

const IMPORTED_MODEL_METADATA_KEY = 'importedModel'

type ObjectMetadataRecord = Record<string, unknown>

/**
 * 将未知值收窄为普通记录对象。
 *
 * @param value 待判断的未知值。
 * @returns 可读写的记录对象；非对象值返回 `undefined`。
 */
function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object'
    ? value as Record<string, unknown>
    : undefined
}

/**
 * 规范化可选字符串，避免空字符串进入导入模型 metadata。
 *
 * @param value 待规范化的未知值。
 * @param shouldTrim 是否裁剪首尾空白。
 * @returns 规范化后的非空字符串；无效值返回 `undefined`。
 */
function normalizeOptionalString(value: unknown, shouldTrim = false): string | undefined {
  if (typeof value !== 'string') {
    return undefined
  }
  const normalized = shouldTrim ? value.trim() : value
  return normalized.length > 0 ? normalized : undefined
}

/**
 * 克隆高亮目标数组，隔离 primitive 索引集合的可变引用。
 *
 * @param targets 源高亮目标数组。
 * @returns 克隆后的高亮目标数组。
 */
function cloneHighlightTargets(targets: EntityHighlightTarget[] | undefined): EntityHighlightTarget[] | undefined {
  return Array.isArray(targets)
    ? targets.map((target) => ({
        objectId: target.objectId,
        primitiveIndices: target.primitiveIndices ? [...target.primitiveIndices] : undefined,
      }))
    : undefined
}

/**
 * 克隆导入层级 meta，避免 clone 链路共享可变集合。
 *
 * @param meta 源导入层级 meta。
 * @returns 克隆后的导入层级 meta。
 */
function cloneHierarchyMeta(
  meta: ImportedModelObjectHierarchyMetadata['meta'],
): ImportedModelObjectHierarchyMetadata['meta'] {
  if (!meta) {
    return undefined
  }

  return {
    semanticKind: meta.semanticKind,
    assemblyNodeId: meta.assemblyNodeId,
    hasGeometry: meta.hasGeometry,
    hasDirectGeometry: meta.hasDirectGeometry,
    pmi: meta.pmi ? { ...meta.pmi } : undefined,
    bodyCount: meta.bodyCount,
    bodyEntries: meta.bodyEntries ? meta.bodyEntries.map((entry) => ({ ...entry })) : undefined,
    bodyPrimitiveIndicesById: meta.bodyPrimitiveIndicesById
      ? Object.fromEntries(
          Object.entries(meta.bodyPrimitiveIndicesById).map(([bodyId, indices]) => [bodyId, [...indices]]),
        )
      : undefined,
    bodyHighlightTargetsById: meta.bodyHighlightTargetsById
      ? Object.fromEntries(
          Object.entries(meta.bodyHighlightTargetsById).map(([bodyId, targets]) => [
            bodyId,
            cloneHighlightTargets(targets) ?? [],
          ]),
        )
      : undefined,
    materialNames: [...(meta.materialNames ?? [])],
    materialName: meta.materialName,
    isFixed: meta.isFixed,
    instanceIndex: meta.instanceIndex,
    configurationName: meta.configurationName,
    displayStateName: meta.displayStateName,
    externalFileUuid: meta.externalFileUuid,
    externalAssemblyName: meta.externalAssemblyName,
    selectionObjectId: meta.selectionObjectId,
    runtimeWrapper: meta.runtimeWrapper,
    highlightTargets: cloneHighlightTargets(meta.highlightTargets),
  }
}

/**
 * 克隆单个 Object3D 对应的导入层级镜像。
 *
 * @param hierarchy 源导入层级镜像。
 * @returns 克隆后的导入层级镜像。
 */
function cloneHierarchy(
  hierarchy: ImportedModelObjectHierarchyMetadata | undefined,
): ImportedModelObjectHierarchyMetadata | undefined {
  if (!hierarchy) {
    return undefined
  }

  return {
    fileId: hierarchy.fileId,
    name: hierarchy.name,
    path: hierarchy.path,
    nodeType: hierarchy.nodeType,
    selectable: hierarchy.selectable,
    parentObjectId: hierarchy.parentObjectId,
    depth: hierarchy.depth,
    meta: cloneHierarchyMeta(hierarchy.meta),
  }
}

/**
 * 克隆导入选择目标，隔离后续阵列/装配重写的可变字段。
 *
 * @param target 源选择目标。
 * @returns 克隆后的选择目标。
 */
function cloneSelectionTarget(
  target: ImportedModelSelectionTargetMetadata | undefined,
): ImportedModelSelectionTargetMetadata | undefined {
  if (!target) {
    return undefined
  }

  return {
    assemblyNodeId: target.assemblyNodeId,
    partUuid: target.partUuid,
    primitiveId: target.primitiveId,
    stepFaceId: target.stepFaceId,
  }
}

/**
 * 克隆 bodyBindingId 到选择目标的覆盖映射。
 *
 * @param targetsByBodyBindingId 源映射。
 * @returns 克隆并过滤非法条目后的映射；无有效条目时返回 `undefined`。
 */
function cloneBodyBindingSelectionTargetsById(
  targetsByBodyBindingId: Record<string, ImportedModelSelectionTargetMetadata> | undefined,
): Record<string, ImportedModelSelectionTargetMetadata> | undefined {
  if (!targetsByBodyBindingId) {
    return undefined
  }

  const cloned: Record<string, ImportedModelSelectionTargetMetadata> = {}
  Object.entries(targetsByBodyBindingId).forEach(([bodyBindingId, target]) => {
    const normalizedBodyBindingId = normalizeOptionalString(bodyBindingId, true)
    const clonedTarget = cloneSelectionTarget(target)
    if (!normalizedBodyBindingId || !clonedTarget?.assemblyNodeId) {
      return
    }
    cloned[normalizedBodyBindingId] = clonedTarget
  })

  return Object.keys(cloned).length > 0 ? cloned : undefined
}

/**
 * 创建导入文件信息的 JSON 安全 metadata 快照。
 *
 * 运行期 FileInfo 可能携带 bigint `size`。Object3D.metadata 会被删除 Undo 持久化到
 * OPFS，因此必须在统一 metadata 入口按协议白名单取值，不能直接展开运行期对象。
 *
 * @param fileInfo 源文件信息或既有 metadata 快照。
 * @returns 仅包含字符串身份字段的 metadata 快照；没有有效字段时返回 undefined。
 */
function createFileInfoMetadataSnapshot(
  fileInfo: ImportedModelFileInfoMetadata | undefined,
): ImportedModelFileInfoMetadata | undefined {
  if (!fileInfo) {
    return undefined
  }

  const fileId = normalizeOptionalString(fileInfo.fileId, true)
  const kind = normalizeOptionalString(fileInfo.kind, true)
  const uuid = normalizeOptionalString(fileInfo.uuid, true)
  const partName = normalizeOptionalString(fileInfo.partName, true)
  const snapshot: ImportedModelFileInfoMetadata = {
    ...(fileId ? { fileId } : {}),
    ...(kind ? { kind } : {}),
    ...(uuid ? { uuid } : {}),
    ...(partName ? { partName } : {}),
  }

  return Object.keys(snapshot).length > 0 ? snapshot : undefined
}

/**
 * 创建导入显示来源定位的 JSON 安全快照。
 *
 * @param displaySource 源显示定位。
 * @returns 规范化后的定位；字段不完整时返回 undefined。
 */
function createDisplaySourceMetadataSnapshot(
  displaySource: PuppyCAD.CompleteGlbDisplaySourceMetadata | undefined,
): PuppyCAD.CompleteGlbDisplaySourceMetadata | undefined {
  if (
    displaySource?.kind !== 'complete-glb'
    || !Number.isInteger(displaySource.nodeIndex)
    || displaySource.nodeIndex < 0
    || !Number.isInteger(displaySource.primitiveIndex)
    || displaySource.primitiveIndex < 0
  ) {
    return undefined
  }

  const meshIndex = typeof displaySource.meshIndex === 'number'
    && Number.isInteger(displaySource.meshIndex)
    && displaySource.meshIndex >= 0
    ? displaySource.meshIndex
    : undefined
  return {
    kind: 'complete-glb',
    nodeIndex: displaySource.nodeIndex,
    ...(meshIndex !== undefined ? { meshIndex } : {}),
    primitiveIndex: displaySource.primitiveIndex,
  }
}

/**
 * 克隆完整 importedModel metadata 片段。
 *
 * @param metadata 源 importedModel metadata。
 * @returns 克隆后的 importedModel metadata。
 */
function cloneImportedModelMetadata(
  metadata: ImportedModelObjectMetadata | undefined,
): ImportedModelObjectMetadata | undefined {
  if (!metadata) {
    return undefined
  }

  return {
    fileId: metadata.fileId,
    hierarchy: cloneHierarchy(metadata.hierarchy),
    selectable: metadata.selectable,
    assemblyNodeId: metadata.assemblyNodeId,
    partFileUuid: metadata.partFileUuid,
    selectionProxyObjectId: metadata.selectionProxyObjectId,
    selectionTarget: cloneSelectionTarget(metadata.selectionTarget),
    bodyBindingSelectionTargetsById: cloneBodyBindingSelectionTargetsById(
      metadata.bodyBindingSelectionTargetsById,
    ),
    fileInfo: createFileInfoMetadataSnapshot(metadata.fileInfo),
    displaySource: createDisplaySourceMetadataSnapshot(metadata.displaySource),
    runtimeWrapper: metadata.runtimeWrapper,
    componentTransformRoot: metadata.componentTransformRoot,
    originalName: metadata.originalName,
  }
}

/**
 * 克隆完整 Object3D.metadata，并对 importedModel 子树做可变引用隔离。
 *
 * @param metadata 源 Object3D.metadata。
 * @returns 克隆后的 Object3D.metadata。
 */
function cloneObjectMetadataRecord(metadata: ObjectMetadataRecord | undefined): ObjectMetadataRecord {
  const cloned: ObjectMetadataRecord = metadata ? { ...metadata } : {}
  const importedModelMetadata = asRecord(metadata?.[IMPORTED_MODEL_METADATA_KEY])
  if (importedModelMetadata) {
    cloned[IMPORTED_MODEL_METADATA_KEY] = cloneImportedModelMetadata(
      importedModelMetadata as ImportedModelObjectMetadata,
    )
  }
  return cloned
}

/**
 * 只读取 Object3D.metadata 上的 importedModel 片段，不做 legacy fallback。
 *
 * @param object 目标 Object3D。
 * @returns 当前对象自身的 importedModel metadata。
 */
function readMetadataOnly(object: THREE.Object3D): ImportedModelObjectMetadata | undefined {
  return asRecord(object.metadata)?.[IMPORTED_MODEL_METADATA_KEY] as ImportedModelObjectMetadata | undefined
}

/**
 * 写入可选字符串字段，空值表示显式清理。
 *
 * @param target 被写入的 importedModel metadata。
 * @param key 字符串字段键。
 * @param value 待写入值。
 */
function writeStringField(
  target: ImportedModelObjectMetadata,
  key: keyof Pick<
    ImportedModelObjectMetadata,
    'fileId' | 'assemblyNodeId' | 'partFileUuid' | 'selectionProxyObjectId' | 'originalName'
  >,
  value: unknown,
): void {
  const normalized = normalizeOptionalString(value, true)
  if (normalized === undefined) {
    delete target[key]
    return
  }
  target[key] = normalized
}

/**
 * 判断 importedModel metadata 是否已经没有任何业务字段。
 *
 * @param metadata 待判断的 importedModel metadata。
 * @returns 是否为空对象。
 */
function isEmptyImportedModelMetadata(metadata: ImportedModelObjectMetadata): boolean {
  return Object.keys(metadata).length === 0
}

/**
 * 从当前对象向父链查找满足业务字段条件的 importedModel metadata。
 *
 * 说明：
 * 1. 新版 assembly runtime 会在 `.step.puppy` 内层节点挂零件局部 metadata；
 * 2. 装配实例身份可能位于更外层 part/subassembly 节点，不能被最近的局部 metadata 截断；
 * 3. 该函数只用于“按字段语义”查找，保留最近 metadata 读取函数给 hierarchy/fileId 等局部语义使用。
 *
 * @param object 起始 Object3D。
 * @param predicate metadata 命中条件。
 * @returns 第一份满足条件的 metadata 原始引用。
 */
function peekMetadataFromSelfOrAncestorsByPredicate(
  object: THREE.Object3D | null | undefined,
  predicate: (metadata: ImportedModelObjectMetadata) => boolean,
): ImportedModelObjectMetadata | undefined {
  let current = object ?? null
  while (current) {
    const metadata = readMetadataOnly(current)
    if (metadata && predicate(metadata)) {
      return metadata
    }
    current = current.parent
  }
  return undefined
}

/**
 * 导入模型 Object3D metadata 统一读写服务。
 *
 * 说明：
 * 1. 该服务是导入模型运行属性的唯一入口；
 * 2. 读取和写入都只使用 `Object3D.metadata.importedModel`，避免继续保留散落 userData 双路径；
 * 3. 几何缓存、拓扑索引、overlay 标记等非语义运行时数据不属于该服务职责。
 */
export class ImportedModelObjectMetadataService {
  /** 全部 imported metadata 语义写入共享的单调修订。 */
  private static semanticRevision = 0
  /** 组件身份专用修订；固定状态、显示属性和相同身份重写不使变换根索引失效。 */
  private static componentIdentityRevision = 0

  /** @returns 组件归属、代理或可写根资格发生变化时推进的单调修订。 */
  public static getComponentIdentityRevision(): number {
    return ImportedModelObjectMetadataService.componentIdentityRevision
  }

  /**
   * 在写入边界捕获组件解析依赖的有限字段，避免为 isFixed 的恢复重建身份索引。
   * @param object 即将写入或已经写入 metadata 的对象。
   * @returns 身份签名；辅助子树统一返回空值，不参与组件索引修订。
   */
  private static captureComponentIdentity(object: THREE.Object3D): string {
    let ancestor: THREE.Object3D | null = object
    while (ancestor) {
      if (SceneAuxiliaryObjectRegistry.isRoot(ancestor) || ancestor.userData?.skipSelectionSubtree === true) return ''
      ancestor = ancestor.parent
    }
    const metadata = readMetadataOnly(object)
    const hierarchy = metadata?.hierarchy?.meta
    // 只序列化少量标量，不复制 Body、几何或材质数组；成员结构由场景订阅另行维护。
    return JSON.stringify([
      Boolean(metadata && Object.keys(metadata).length),
      metadata?.assemblyNodeId, metadata?.selectionTarget?.assemblyNodeId,
      metadata?.selectionProxyObjectId, metadata?.componentTransformRoot,
      hierarchy?.semanticKind, hierarchy?.hasDirectGeometry, hierarchy?.selectionObjectId,
    ])
  }

  /**
   * 读取 imported 第一阶段候选资格依赖的 metadata 修订。
   *
   * @returns 任一 Object3D imported metadata 写入后都会递增的单调值。
   */
  public static getSemanticRevision(): number {
    return ImportedModelObjectMetadataService.semanticRevision
  }

  /**
   * 读取旧 GLB body entry 协议声明的稳定 bodyBindingId。
   *
   * 两组数组仍是 GLB 解析与 primitive 映射的兼容输入，但业务模块不应重复理解它们的
   * 容错和去重规则。该方法只读且保持首次出现顺序，便于图验证与拓扑收集复用。
   *
   * @param object 需要读取 body 绑定的场景对象。
   * @returns 已去空白、按首次出现顺序去重的绑定 ID。
   */
  public static collectBodyBindingIds(object: THREE.Object3D): readonly string[] {
    const userData = asRecord(object.userData)
    const bodyBindingIds: string[] = []
    const knownBodyBindingIds = new Set<string>()

    ;[
      userData?.importedPartBodyEntries,
      userData?.primitiveEntityEntries,
    ].forEach((entries) => {
      if (!Array.isArray(entries)) {
        return
      }
      entries.forEach((entry) => {
        const bodyBindingId = asRecord(entry)?.bodyBindingId
        if (typeof bodyBindingId !== 'string') {
          return
        }
        const normalizedBodyBindingId = bodyBindingId.trim()
        if (!normalizedBodyBindingId || knownBodyBindingIds.has(normalizedBodyBindingId)) {
          return
        }
        knownBodyBindingIds.add(normalizedBodyBindingId)
        bodyBindingIds.push(normalizedBodyBindingId)
      })
    })

    return bodyBindingIds
  }

  /**
   * 读取 Puppy 导出树当前对象自身的 owner metadata。
   *
   * 返回浅副本，调用方不能绕过统一写入口原地修改 `userData.puppy`。
   *
   * @param object 目标 Object3D。
   * @returns Puppy owner metadata 副本；当前对象未声明时返回 undefined。
   */
  public static readPuppyOwnerMetadata(
    object: THREE.Object3D,
  ): Readonly<Record<string, unknown>> | undefined {
    const puppy = asRecord(asRecord(object.userData)?.puppy)
    return puppy ? { ...puppy } : undefined
  }

  /**
   * 合并写入 Puppy 导出树 owner metadata。
   *
   * 该入口保留 Object3D 其它 userData 与 Puppy 扩展字段；patch 中值为 undefined 的字段会被
   * 显式删除，供组件根从显示节点继承 metadata 后移除 `displayNodeId`。
   *
   * @param object 目标 Object3D。
   * @param patch 待合并的 owner 字段。
   */
  public static writePuppyOwnerMetadata(
    object: THREE.Object3D,
    patch: Readonly<Record<string, unknown>>,
  ): void {
    const userData = asRecord(object.userData) ?? {}
    const puppy = { ...(asRecord(userData.puppy) ?? {}) }
    Object.entries(patch).forEach(([key, value]) => {
      if (value === undefined) {
        delete puppy[key]
        return
      }
      puppy[key] = value
    })
    object.userData = {
      ...userData,
      ...(Object.keys(puppy).length > 0 ? { puppy } : {}),
    }
    if (Object.keys(puppy).length === 0) {
      delete object.userData.puppy
    }
  }

  /**
   * 写入或清除导入模型 metadata 字段。
   *
   * @param object 目标 Object3D。
   * @param metadata 待写入的导入模型 metadata 片段。
   */
  public static writeMetadata(
    object: THREE.Object3D,
    metadata: Partial<ImportedModelObjectMetadata>,
  ): void {
    const previousComponentIdentity = ImportedModelObjectMetadataService.captureComponentIdentity(object)
    const objectMetadata = asRecord(object.metadata) ?? {}
    const current = cloneImportedModelMetadata(readMetadataOnly(object)) ?? {}

    if ('fileId' in metadata) {
      writeStringField(current, 'fileId', metadata.fileId)
    }
    if ('hierarchy' in metadata) {
      const hierarchy = cloneHierarchy(metadata.hierarchy)
      if (hierarchy) {
        current.hierarchy = hierarchy
      } else {
        delete current.hierarchy
      }
    }
    if ('selectable' in metadata) {
      if (typeof metadata.selectable === 'boolean') {
        current.selectable = metadata.selectable
      } else {
        delete current.selectable
      }
    }
    if ('assemblyNodeId' in metadata) {
      writeStringField(current, 'assemblyNodeId', metadata.assemblyNodeId)
    }
    if ('partFileUuid' in metadata) {
      writeStringField(current, 'partFileUuid', metadata.partFileUuid)
    }
    if ('selectionProxyObjectId' in metadata) {
      writeStringField(current, 'selectionProxyObjectId', metadata.selectionProxyObjectId)
    }
    if ('selectionTarget' in metadata) {
      const selectionTarget = cloneSelectionTarget(metadata.selectionTarget)
      if (selectionTarget?.assemblyNodeId) {
        current.selectionTarget = selectionTarget
      } else {
        delete current.selectionTarget
      }
    }
    if ('bodyBindingSelectionTargetsById' in metadata) {
      const bodyBindingSelectionTargetsById = cloneBodyBindingSelectionTargetsById(
        metadata.bodyBindingSelectionTargetsById,
      )
      if (bodyBindingSelectionTargetsById) {
        current.bodyBindingSelectionTargetsById = bodyBindingSelectionTargetsById
      } else {
        delete current.bodyBindingSelectionTargetsById
      }
    }
    if ('fileInfo' in metadata) {
      const fileInfo = createFileInfoMetadataSnapshot(metadata.fileInfo)
      if (fileInfo) {
        current.fileInfo = fileInfo
      } else {
        delete current.fileInfo
      }
    }
    if ('runtimeWrapper' in metadata) {
      if (typeof metadata.runtimeWrapper === 'boolean') {
        current.runtimeWrapper = metadata.runtimeWrapper
      } else {
        delete current.runtimeWrapper
      }
    }
    if ('displaySource' in metadata) {
      const displaySource = createDisplaySourceMetadataSnapshot(metadata.displaySource)
      if (displaySource) {
        current.displaySource = displaySource
      } else {
        delete current.displaySource
      }
    }
    if ('componentTransformRoot' in metadata) {
      if (typeof metadata.componentTransformRoot === 'boolean') {
        current.componentTransformRoot = metadata.componentTransformRoot
      } else {
        delete current.componentTransformRoot
      }
    }
    if ('originalName' in metadata) {
      writeStringField(current, 'originalName', metadata.originalName)
    }

    if (isEmptyImportedModelMetadata(current)) {
      delete objectMetadata[IMPORTED_MODEL_METADATA_KEY]
    } else {
      objectMetadata[IMPORTED_MODEL_METADATA_KEY] = current
    }
    object.metadata = objectMetadata
    ImportedModelObjectMetadataService.semanticRevision += 1
    if (previousComponentIdentity !== ImportedModelObjectMetadataService.captureComponentIdentity(object)) {
      ImportedModelObjectMetadataService.componentIdentityRevision += 1
    }
  }

  /**
   * 只读取当前对象自身的 metadata，不包含 legacy userData fallback。
   *
   * @param object 目标 Object3D。
   * @returns 当前对象自身 metadata 副本。
   */
  public static readOwnMetadata(object: THREE.Object3D): ImportedModelObjectMetadata | undefined {
    return cloneImportedModelMetadata(readMetadataOnly(object))
  }

  /**
   * 只读窥探当前对象自身的 metadata 原始引用。
   *
   * 设计说明：
   * 1. hover、候选集、碰撞检测等热路径只需要判断字段，不需要隔离副本；
   * 2. 返回原始引用可以避免每次鼠标移动深拷贝 body/highlight 数组；
   * 3. 调用方不得修改返回对象，任何写入都必须继续走 `writeMetadata`。
   *
   * @param object 目标 Object3D。
   * @returns 当前对象自身 metadata 原始引用。
   */
  public static peekOwnMetadata(object: THREE.Object3D): ImportedModelObjectMetadata | undefined {
    return readMetadataOnly(object)
  }

  /**
   * 读取当前对象自身的导入模型 metadata。
   *
   * @param object 目标 Object3D。
   * @returns 当前对象自身导入模型属性。
   */
  public static readMetadata(object: THREE.Object3D): ImportedModelObjectMetadata | undefined {
    return ImportedModelObjectMetadataService.readOwnMetadata(object)
  }

  /**
   * 只读窥探当前对象自身的导入模型 metadata。
   *
   * @param object 目标 Object3D。
   * @returns 当前对象自身 metadata 原始引用。
   */
  public static peekMetadata(object: THREE.Object3D): ImportedModelObjectMetadata | undefined {
    return ImportedModelObjectMetadataService.peekOwnMetadata(object)
  }

  /**
   * 从当前对象向父链读取第一个导入模型 metadata。
   *
   * @param object 起始 Object3D。
   * @returns 父链上第一个导入模型 metadata。
   */
  public static readMetadataFromSelfOrAncestors(
    object: THREE.Object3D | null | undefined,
  ): ImportedModelObjectMetadata | undefined {
    let current = object ?? null
    while (current) {
      const metadata = ImportedModelObjectMetadataService.readMetadata(current)
      if (metadata) {
        return metadata
      }
      current = current.parent
    }
    return undefined
  }

  /**
   * 从当前对象向父链只读窥探第一份导入模型 metadata。
   *
   * @param object 起始 Object3D。
   * @returns 父链上第一份 metadata 原始引用。
   */
  public static peekMetadataFromSelfOrAncestors(
    object: THREE.Object3D | null | undefined,
  ): ImportedModelObjectMetadata | undefined {
    let current = object ?? null
    while (current) {
      const metadata = ImportedModelObjectMetadataService.peekMetadata(current)
      if (metadata) {
        return metadata
      }
      current = current.parent
    }
    return undefined
  }

  /**
   * 判断对象或父链是否属于 imported-model。
   *
   * @param object 待判断 Object3D。
   * @returns 是否属于 imported-model 场景树。
   */
  public static isImportedModelObject(object: THREE.Object3D | null | undefined): boolean {
    return Boolean(ImportedModelObjectMetadataService.peekMetadataFromSelfOrAncestors(object))
  }

  /**
   * 读取当前对象自身的导入层级镜像。
   *
   * @param object 目标 Object3D。
   * @returns 导入层级镜像。
   */
  public static resolveHierarchy(object: THREE.Object3D): ImportedModelObjectHierarchyMetadata | undefined {
    return ImportedModelObjectMetadataService.peekMetadata(object)?.hierarchy
  }

  /**
   * 从当前对象向父链读取导入层级镜像。
   *
   * @param object 起始 Object3D。
   * @returns 导入层级镜像。
   */
  public static resolveHierarchyFromSelfOrAncestors(
    object: THREE.Object3D | null | undefined,
  ): ImportedModelObjectHierarchyMetadata | undefined {
    return ImportedModelObjectMetadataService.peekMetadataFromSelfOrAncestors(object)?.hierarchy
  }

  /**
   * 读取当前对象自身的文件 ID。
   *
   * @param object 目标 Object3D。
   * @returns GLB 文件 ID。
   */
  public static resolveFileId(object: THREE.Object3D): string | undefined {
    return ImportedModelObjectMetadataService.peekMetadata(object)?.fileId
  }

  /**
   * 读取当前对象自身的选择代理 ID。
   *
   * @param object 目标 Object3D。
   * @returns 选择代理 Object3D uuid。
   */
  public static resolveSelectionProxyObjectId(object: THREE.Object3D): string | undefined {
    return ImportedModelObjectMetadataService.peekMetadata(object)?.selectionProxyObjectId
  }

  /**
   * 临时取走当前对象 metadata 中的选择代理 ID。
   *
   * 设计说明：
   * 1. 装配引用零件生成 hierarchy 时需要暂时忽略模板上的选择代理，否则会污染实例层级；
   * 2. 选择代理已经收口到 `Object3D.metadata.importedModel`，这里不再消费 legacy userData；
   * 3. 调用方必须在 `finally` 中通过 `writeMetadata` 恢复返回值，保证异常路径不丢失选择代理关系。
   *
   * @param object 目标 Object3D。
   * @returns 被取走的选择代理 Object3D uuid。
   */
  public static takeSelectionProxyObjectId(object: THREE.Object3D): string | undefined {
    const metadataProxyObjectId = ImportedModelObjectMetadataService.peekOwnMetadata(object)?.selectionProxyObjectId
    if (metadataProxyObjectId) {
      ImportedModelObjectMetadataService.writeMetadata(object, { selectionProxyObjectId: undefined })
    }

    return metadataProxyObjectId
  }

  /**
   * 读取当前对象自身 hierarchy meta 中的 selectionObjectId。
   *
   * @param object 目标 Object3D。
   * @returns hierarchy 内声明的选择对象 uuid。
   */
  public static resolveHierarchySelectionObjectId(object: THREE.Object3D): string | undefined {
    return ImportedModelObjectMetadataService.peekMetadata(object)?.hierarchy?.meta?.selectionObjectId
  }

  /**
   * 读取当前对象自身的导入选择目标。
   *
   * @param object 目标 Object3D。
   * @returns 导入选择目标。
   */
  public static resolveSelectionTarget(object: THREE.Object3D): ImportedModelSelectionTargetMetadata | undefined {
    return ImportedModelObjectMetadataService.peekMetadata(object)?.selectionTarget
  }

  /**
   * 从当前对象向父链读取导入选择目标。
   *
   * @param object 起始 Object3D。
   * @returns 导入选择目标。
   */
  public static resolveSelectionTargetFromSelfOrAncestors(
    object: THREE.Object3D | null | undefined,
  ): ImportedModelSelectionTargetMetadata | undefined {
    return peekMetadataFromSelfOrAncestorsByPredicate(
      object,
      (metadata) => Boolean(metadata.selectionTarget?.assemblyNodeId),
    )?.selectionTarget
  }

  /**
   * 按 bodyBindingId 从当前对象或父链读取装配拓扑选择目标覆盖。
   *
   * @param object 起始 Object3D。
   * @param bodyBindingId imported body 绑定 ID。
   * @returns body 级选择目标；不存在时返回 `undefined`。
   */
  public static resolveBodyBindingSelectionTargetFromSelfOrAncestors(
    object: THREE.Object3D | null | undefined,
    bodyBindingId: string | null | undefined,
  ): ImportedModelSelectionTargetMetadata | undefined {
    const normalizedBodyBindingId = normalizeOptionalString(bodyBindingId, true)
    if (!normalizedBodyBindingId) {
      return undefined
    }
    const lookupKeys = createImportedBodyOwnershipLookupKeys(normalizedBodyBindingId)
    const stableBodyBindingId = resolveStableImportedBodyBindingId(normalizedBodyBindingId)

    let current = object ?? null
    while (current) {
      const metadata = ImportedModelObjectMetadataService.peekMetadata(current)
      const targetsByBodyBindingId = metadata?.bodyBindingSelectionTargetsById
      if (targetsByBodyBindingId) {
        for (const lookupKey of lookupKeys) {
          const clonedTarget = cloneSelectionTarget(targetsByBodyBindingId[lookupKey])
          if (clonedTarget?.assemblyNodeId) {
            return clonedTarget
          }
        }

        /**
         * 离线包重载后 Mate 可能保存稳定 bodyBindingId，而运行时为共享 mesh 追加了后缀。
         * 这里只在同一层 metadata 内找到唯一目标时才按稳定 ID 回退，避免多实例共享 body 时误把
         * 另一个组件的选择目标返回给当前引用。
         */
        const lookupKeySet = new Set(lookupKeys)
        const stableMatches = Object.entries(targetsByBodyBindingId)
          .filter(([targetBodyBindingId]) => (
            resolveStableImportedBodyBindingId(targetBodyBindingId) === stableBodyBindingId
            || createImportedBodyOwnershipLookupKeys(targetBodyBindingId).some((lookupKey) => (
              lookupKeySet.has(lookupKey)
            ))
          ))
          .map(([, target]) => cloneSelectionTarget(target))
          .filter((target): target is ImportedModelSelectionTargetMetadata => Boolean(target?.assemblyNodeId));
        const uniqueAssemblyNodeIds = new Set(stableMatches.map((target) => target.assemblyNodeId))
        if (stableMatches.length === 1 || uniqueAssemblyNodeIds.size === 1) {
          return stableMatches[0]
        }
      }
      current = current.parent
    }

    return undefined
  }

  /**
   * 读取当前对象自身的装配节点 ID。
   *
   * @param object 目标 Object3D。
   * @returns 装配节点 ID。
   */
  public static resolveAssemblyNodeId(object: THREE.Object3D): string | undefined {
    return ImportedModelObjectMetadataService.peekMetadata(object)?.assemblyNodeId
  }

  /**
   * 从当前对象向父链读取装配节点 ID。
   *
   * @param object 起始 Object3D。
   * @returns 装配节点 ID。
   */
  public static resolveAssemblyNodeIdFromSelfOrAncestors(
    object: THREE.Object3D | null | undefined,
  ): string | undefined {
    const metadata = peekMetadataFromSelfOrAncestorsByPredicate(
      object, (item) => Boolean(item.selectionTarget?.assemblyNodeId ?? item.assemblyNodeId),
    )
    return metadata?.selectionTarget?.assemblyNodeId ?? metadata?.assemblyNodeId
  }

  /**
   * 读取当前对象自身引用的零件或子装配 UUID。
   *
   * @param object 目标 Object3D。
   * @returns 零件或子装配 UUID。
   */
  public static resolvePartFileUuid(object: THREE.Object3D): string | undefined {
    return ImportedModelObjectMetadataService.peekMetadata(object)?.partFileUuid
  }

  /**
   * 从当前对象向父链读取零件或子装配 UUID。
   *
   * @param object 起始 Object3D。
   * @returns 零件或子装配 UUID。
   */
  public static resolvePartFileUuidFromSelfOrAncestors(
    object: THREE.Object3D | null | undefined,
  ): string | undefined {
    return peekMetadataFromSelfOrAncestorsByPredicate(
      object,
      (metadata) => Boolean(metadata.partFileUuid),
    )?.partFileUuid
  }

  /**
   * 读取当前对象自身的原始 GLTF 节点名。
   *
   * @param object 目标 Object3D。
   * @returns 原始 GLTF 节点名。
   */
  public static resolveOriginalName(object: THREE.Object3D): string | undefined {
    return ImportedModelObjectMetadataService.peekMetadata(object)?.originalName
  }

  /**
   * 读取当前 Mesh 自身的稳定显示来源定位。
   *
   * 显示定位不能沿父链继承：多 primitive node 的每个子 Mesh 必须保留各自 primitiveIndex。
   *
   * @param object 目标 Object3D。
   * @returns 当前对象自身的显示来源定位。
   */
  public static resolveDisplaySource(
    object: THREE.Object3D,
  ): PuppyCAD.CompleteGlbDisplaySourceMetadata | undefined {
    return ImportedModelObjectMetadataService.peekMetadata(object)?.displaySource
  }

  /**
   * 复制完整 Object3D.metadata，并对 importedModel 字段进行可变数据隔离。
   *
   * @param source 源 Object3D。
   * @param target 目标 Object3D。
   */
  public static copyObjectMetadata(source: THREE.Object3D, target: THREE.Object3D): void {
    const previousComponentIdentity = ImportedModelObjectMetadataService.captureComponentIdentity(target)
    target.metadata = cloneObjectMetadataRecord(asRecord(source.metadata))
    ImportedModelObjectMetadataService.semanticRevision += 1
    if (previousComponentIdentity !== ImportedModelObjectMetadataService.captureComponentIdentity(target)) {
      ImportedModelObjectMetadataService.componentIdentityRevision += 1
    }
  }
}
